import { AdminProtocolsStorage } from './../../shared/admin-protocols-storage';
import { Settings } from './../../../core/settings';
import { isEmpty } from 'lodash';
import { FormControl } from '@angular/forms';
import { ChangeDetectionStrategy, Component, OnDestroy, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { ColDef } from 'ag-grid-community';
import { Observable, Subscription, of } from 'rxjs';
import { distinctUntilChanged, flatMap, map, publishReplay, refCount, filter, tap, switchMap } from 'rxjs/operators';
import { FilterFieldValue } from '../../../covance-filter';
import { DOCUMENTS_STORAGE } from '../../../documents/documents-storage';
import { FILTER_ITEM_LOADER } from '../../../documents/filter-items-loader';
import { UrlBuilder } from '../../../core/index';
import { CategoriesStorage, Category, CategoryNaturalKey } from '../../../documents';
import { AdminActivityReportDocumentsStorage } from './admin-activity-report-documents-storage';
import { AdminActivityReportFilterItemsStorage } from './admin-activity-report-filter-items-storage';
import { AdminActivityReportsDocumentsColumns } from './admin-activity-reports-documents-columns';
import { AdminActivityReportsFilterResolver } from './admin-activity-reports-filter-resolver';
import { routeParams } from '../../../shared/route-helpers';
import { FILTER_RESOLVER } from '../../../documents/filter-resolver';
import { MandatoryFilter } from '../../../documents/mandatory-filter';
import { Protocol } from '../../shared';
import { siteFilter, projectFilter } from '../../../documents/document-filter-definitions';

@Component({
    selector: 'admin-activity-report-documents',
    template: require('./admin-activity-report-documents.component.html'),
    styles: [require('./admin-activity-report-documents.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: FILTER_ITEM_LOADER, useExisting: AdminActivityReportFilterItemsStorage },
        { provide: DOCUMENTS_STORAGE, useExisting: AdminActivityReportDocumentsStorage },
        { provide: FILTER_RESOLVER, useExisting: AdminActivityReportsFilterResolver },
    ]
})
export class AdminActivityReportDocumentsComponent implements AfterViewInit, OnDestroy {

    public categories: Observable<Category[]>;
    public selectedCategory: Observable<Category>;
    public selectedCategoryId: Observable<CategoryNaturalKey>;
    public columns: Observable<ColDef[]>;
    public filterFieldDefs: Observable<FilterFieldValue[]>;
    public adminFilterGroup: Observable<string>;
    public readonly protocolFilterName = MandatoryFilter.Protocol;
    public readonly protocolControl = new FormControl();
    public subscription = new Subscription();
    public readonly warningThreshhold: number;
    public mandatoryFilterValue$: Observable<string>;
    
    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private categoriesStorage: CategoriesStorage,
        private documentsColumns: AdminActivityReportsDocumentsColumns,
        private urlBuilder: UrlBuilder,
        private protocolsStorage: AdminProtocolsStorage,
        settings: Settings,
    ) { 
        this.warningThreshhold = settings.sqlRequestLimitForActivityReports;
        this.mandatoryFilterValue$ = this.protocolControl.valueChanges
            .pipe(
                map(v => v.toString())
            );
    }

    public ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    public ngAfterViewInit() {
        this.categories = this.categoriesStorage.activityReportCategories;

        this.selectedCategoryId = this.route.params.pipe(
            map(x => x[routeParams.categoryId] as CategoryNaturalKey | undefined),
            distinctUntilChanged(),
            publishReplay(1),
            refCount()
        );

        this.selectedCategory = this.selectedCategoryId.pipe(
            flatMap(categoryId => this.categoriesStorage.findCategoryById(categoryId as CategoryNaturalKey)),
            publishReplay(1),
            refCount(),
        );

        this.columns = this.documentsColumns.getColumnDefinitions(this.selectedCategory);

        this.subscription.add(
                this.route.queryParams
                    .pipe(
                        map(p => p[this.protocolFilterName] as string),
                        switchMap(protocolId => {
                            const oldProtocolId = isEmpty(this.protocolControl.value) ? null : this.protocolControl.value[0].id.toString();
                            if (oldProtocolId !== protocolId) {
                                return this.protocolsStorage.searchById(protocolId);
                            }
                            return of(null);
                        }),
                        filter(v => !!v),
                        tap((protocol: Protocol[]) => {
                            this.protocolControl.setValue(protocol);
                        })
                    ).subscribe()
        );
    }

    public categoryChanged(categoryId: CategoryNaturalKey) {
        this.urlBuilder.esa().admin().activityReports().documents(categoryId).navigate();
    }

    public onProtocolsChange(protocols: Protocol[]) {
        if (isEmpty(protocols)) {
            return;
        }

        const queryParam: Params = {};
        queryParam[siteFilter.queryParam] = null;
        queryParam[projectFilter.queryParam] = null;

        queryParam[this.protocolFilterName] = protocols[0].id;
        this.router.navigate(
            [],
            {
                relativeTo: this.route,
                queryParams: queryParam,
                queryParamsHandling: 'merge',
            });
    }
}